import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Copy, Check, MapPin, Store, UtensilsCrossed, Star } from "lucide-react";
import { toast } from "sonner";

const TITLE = "Doporučitel — přidejte podnik, menu a recenzi | Cerveza.International";
const DESCRIPTION =
  "Veřejná stránka pro doporučitele: přidejte podnik, položky menu a recenze. Vše se po schválení zobrazí na mapě Cerveza.International.";

export const Route = createFileRoute("/doporucitel")({
  component: DoporucitelPage,
  head: () => {
    const url = "https://cerveza.international/doporucitel";
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
});

function DoporucitelPage() {
  const { user } = useAuth();
  const [code, setCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [origin, setOrigin] = useState("https://cerveza.international");

  useEffect(() => {
    if (typeof window !== "undefined") setOrigin(window.location.origin);
  }, []);

  useEffect(() => {
    if (!user) { setCode(null); return; }
    let cancelled = false;
    void supabase
      .from("profiles")
      .select("referral_code")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setCode((data?.referral_code as string | null) ?? null);
      });
    return () => { cancelled = true; };
  }, [user]);

  const link = code ? `${origin}/r/${code}` : "";

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Odkaz zkopírován");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Kopírování se nepodařilo");
    }
  };

  const steps = [
    {
      icon: Store,
      title: "Přidejte podnik",
      text: "Vyplňte název, adresu a PSČ — souřadnice doplníme automaticky. Po schválení se podnik objeví na mapě.",
      to: "/registrace-podniku",
      cta: "Přidat podnik",
    },
    {
      icon: UtensilsCrossed,
      title: "Přidejte položky menu",
      text: "U svých podniků vytvořte digitální menu s kategoriemi, cenami a alergeny. Publikované menu je dostupné z profilu podniku i z mapy.",
      to: "/moje-podniky",
      cta: "Spravovat menu",
    },
    {
      icon: Star,
      title: "Napište recenzi",
      text: "Ohodnoťte navštívený podnik 1–10 hvězdičkami a přidejte komentář. Hodnocení se ukazuje v popupu na mapě i na profilu podniku.",
      to: "/mapa",
      cta: "Najít podnik na mapě",
    },
  ];

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10">
      <header className="mb-8">
        <Badge variant="outline" className="mb-3">Pro doporučitele</Badge>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          Přidávejte podniky, menu a recenze — objeví se na mapě
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Tato stránka je veřejná. Prohlédnout si ji může kdokoli, pro přidání obsahu
          stačí bezplatná registrace. Každý příspěvek se po schválení propíše do veřejné
          mapy Cerveza.International.
        </p>
      </header>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="text-lg">Váš osobní odkaz doporučitele</CardTitle>
          <CardDescription>
            Kdo se zaregistruje přes tento odkaz, přiřadí se do vaší struktury doporučení.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {user && link ? (
            <div className="flex flex-wrap items-center gap-2">
              <code className="rounded bg-muted px-3 py-2 text-sm">{link}</code>
              <Button size="sm" variant="secondary" onClick={copy}>
                {copied ? <Check className="mr-1 h-4 w-4" /> : <Copy className="mr-1 h-4 w-4" />}
                Kopírovat
              </Button>
            </div>
          ) : user ? (
            <p className="text-sm text-muted-foreground">Váš kód se právě připravuje — obnovte stránku za okamžik.</p>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-muted-foreground">Přihlaste se a získejte svůj odkaz.</p>
              <Button asChild size="sm">
                <Link to="/auth" search={{ redirect: "/doporucitel" } as never}>Přihlásit se</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <section aria-labelledby="co-muzete-pridat">
        <h2 id="co-muzete-pridat" className="mb-4 text-xl font-semibold">Co můžete přidat</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((s) => (
            <Card key={s.title} className="flex flex-col">
              <CardHeader>
                <s.icon className="mb-2 h-6 w-6 text-primary" aria-hidden />
                <CardTitle className="text-base">{s.title}</CardTitle>
                <CardDescription>{s.text}</CardDescription>
              </CardHeader>
              <CardContent className="mt-auto">
                <Button asChild size="sm" variant="secondary">
                  <Link to={s.to}>{s.cta}</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-5 w-5 text-primary" aria-hidden />
            Jak se obsah dostane na mapu
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>1. Přidáte podnik → záznam čeká na schválení moderátorem.</p>
          <p>2. Po schválení se podnik zobrazí na mapě s vaší adresou a hodnocením.</p>
          <p>3. Publikované menu a recenze se propisují do popupu podniku na mapě.</p>
          <Button asChild size="sm" className="mt-2">
            <Link to="/mapa">Otevřít mapu</Link>
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
